import * as http from 'node:http';
import { closeHttpServer } from '../../http/close-http-server.js';
import { createLocalShortcutRequestHandler } from './server.js';
import type { LocalShortcutServerContext, LocalShortcutServerHandle } from './types.js';

const RESTART_DELAY_MS = 250;
const MAX_RESTART_ATTEMPTS = 5;

function listenOnPort(server: http.Server, port: number): Promise<number> {
  return new Promise((resolve, reject) => {
    server.once('error', reject);
    server.listen(port, '127.0.0.1', () => {
      const addr = server.address();
      if (!addr || typeof addr === 'string') {
        reject(new Error('Local shortcut server did not bind'));
        return;
      }
      resolve(addr.port);
    });
  });
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function startLocalShortcutServer(ctx: LocalShortcutServerContext): Promise<LocalShortcutServerHandle> {
  let closed = false;
  let restartAttempts = 0;
  let currentServer: http.Server | null = null;
  let boundPort: number | null = null;
  let restarting: Promise<void> | null = null;

  async function bindServer(port: number): Promise<number> {
    const server = http.createServer(createLocalShortcutRequestHandler(ctx));
    server.on('error', (err) => {
      void restartAfterError(err);
    });
    currentServer = server;
    const listenPort = await listenOnPort(server, port);
    boundPort = listenPort;
    return listenPort;
  }

  async function restartAfterError(_reason: unknown): Promise<void> {
    if (closed || restarting) return;
    if (restartAttempts >= MAX_RESTART_ATTEMPTS) return;
    restartAttempts += 1;
    restarting = (async () => {
      await closeHttpServer(currentServer);
      currentServer = null;
      if (closed) return;
      await delay(RESTART_DELAY_MS);
      if (closed) return;
      try {
        await bindServer(boundPort ?? 0);
        restartAttempts = 0;
      } catch {
        /* next server error will retry until max attempts */
      }
    })().finally(() => {
      restarting = null;
    });
    await restarting;
  }

  return bindServer(0).then((port) => ({
    port,
    token: ctx.token,
    close: async () => {
      closed = true;
      await closeHttpServer(currentServer);
      currentServer = null;
    },
  }));
}
